<template>
  <div :class="['floating-label', { filled: isFilled }]">
    <label class="floating-label__text" :for="inputId">
      {{ custom.name }}
      <span v-if="custom.required" class="required required-span">*</span>
      <template v-if="additionalCost">
        (+ {{ formatPrice(additionalCost) }})
      </template>
    </label>
    <input
      v-if="customType === 'date'"
      :id="inputId"
      v-model="inputValue"
      type="date"
      :name="inputName"
      :required="custom.required"
      :class="[
        'floating-label__input',
        'floating-label__input--white',
        'customization-field',
        {
          required: custom.required,
          'customization-required': custom.required,
          error: hasErrors,
        },
      ]"
      :data-code="custom.code"
      :data-additional-cost="custom.price"
    />
    <textarea
      v-else-if="customType === 'message'"
      :id="inputId"
      v-model="inputValue"
      :name="inputName"
      required="custom.required"
      :class="[
        'customization-field',
        'customization-textarea',
        {
          required: custom.required,
          'customization-required': custom.required,
          error: hasErrors,
        },
      ]"
      :data-code="custom.code"
      :data-additional-cost="custom.price"
      rows="5"
      @focus="focused = true"
      @blur="focused = false"
    >
    </textarea>
    <input
      v-else
      :id="inputId"
      v-model="inputValue"
      type="text"
      :name="inputName"
      :maxlength="custom.max_length"
      :required="custom.required"
      :class="[
        'floating-label__input',
        'floating-label__input--white',
        'only-capitalize',
        'customization-field',
        {
          required: custom.required,
          'customization-required': custom.required,
          error: hasErrors,
        },
      ]"
      :data-code="custom.code"
      :data-additional-cost="custom.price"
      @focus="focused = true"
      @blur="focused = false"
    />
    <div v-if="hasErrors" class="form__field-error">
      {{ errors.join(' ') }}
    </div>
  </div>
</template>

<script>
import BaseInput from '~/components/product/form/BaseInput'
import priceable from '~/mixins/priceable'
import isPresent from '~/utils/isPresent'

const SESSION_CUSTOM_KEY = 'session_customization_'

export default {
  name: 'CustomTextInput',
  extends: BaseInput,
  mixins: [priceable],
  props: {
    customType: {
      type: String,
      required: true,
    },
    custom: {
      type: Object,
      required: true,
    },
    lineItemCustom: {
      type: Object,
      default: () => {},
    },
    customInputId: {
      type: String,
      default: null,
    },
    customInputName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      focused: false,
    }
  },
  computed: {
    isFilled() {
      return this.focused || this.inputValue || this.customType === 'date'
    },
    additionalCost() {
      return this.parseNumber(this.custom.additional_cost)
    },
    inputName() {
      if (this.customInputName) {
        return `${this.customInputName}[${this.custom.id}]`
      } else {
        return `customizations[${this.custom.id}]`
      }
    },
    inputId() {
      if (this.customInputId) {
        return `${this.customInputId}_${this.custom.id}`
      } else {
        return `customization_${this.custom.id}`
      }
    },
  },
  watch: {
    inputValue(newValue) {
      const cleanedValue = this.clearValue(newValue)

      if (cleanedValue !== newValue) {
        this.inputValue = cleanedValue
      }

      sessionStorage.setItem(
        `${SESSION_CUSTOM_KEY}${this.custom.code}`,
        this.inputValue
      )
    },
  },
  created() {
    if (isPresent(this.lineItemCustom)) {
      this.inputValue = this.lineItemCustom[this.custom.name]
    }
  },
  beforeMount() {
    const userCustom = sessionStorage.getItem(
      `${SESSION_CUSTOM_KEY}${this.custom.code}`
    )

    if (
      userCustom &&
      !this.lineItemCustom?.[this.custom.name] &&
      (!this.inputValue ||
        this.inputValue === this.lineItemCustom?.[this.custom.name])
    ) {
      this.inputValue = this.clearValue(userCustom)
    }
  },
  methods: {
    /** Removes undesired characters from input value */
    clearValue(value) {
      let cleanedValue = value

      if (!cleanedValue) return ''

      /** Removes any accentuation character from the value */
      if (!this.custom.allow_accentuation) {
        cleanedValue = cleanedValue.replace(/[\u00C0-\u024F\u1E00-\u1EFF]+/, '')
      }

      /** Downcase only */
      if (!this.custom.allow_uppercase) {
        cleanedValue = cleanedValue.toLowerCase()
      }

      return cleanedValue
    },
  },
}
</script>
